<template>
  <div id="dashboard-wrapper">
    <heading :heading="headingText" />
    <div id="alert-scroller">
      <div class="scroll-mask" :style="mask"></div>
      <component v-for="(card, index) in alerts" :key="index" :is="card" />
      <div class="scroll-padding"></div>
    </div>
    <div id="dashboard-bottom-half">
      <upcoming-events
        id="upcoming-events"
        :heading="`Upcoming Events (${events.length})`"
      />
      <chart
        id="chart-container"
        container="chart-container"
        :formatter="chartFormatter"
        :chartData="chartData"
      />
      <to-do v-if="!tenant" id="todo-list" heading="To-Dos" :toDos="todos" />
    </div>
  </div>
</template>
<script>
import Heading from "../../Components/UI/Heading.vue";
// import AlertCard from "../../Components/UI/AlertCard.vue";
import Chart from "../../Components/Dashboard/Chart/Chart.vue";
import UpcomingEvents from "../../Components/UpcomingEvents.vue";
import NoAlerts from "./DashboardAlertCards/NoAlerts.vue";
import NewBookings from "./DashboardAlertCards/NewBookings.vue";
import InvoicesOverdue from "./DashboardAlertCards/InvoicesOverdue.vue";
import MonthlyRevenueChange from "./DashboardAlertCards/MonthlyRevenueChange.vue";
import ContractsUnsigned from "./DashboardAlertCards/ContractsUnsigned.vue";
import DaysRemaining from "./DashboardAlertCards/DaysRemaining.vue";
import ToDo from "../../Components/ToDo/ToDoList.vue";
import {
  total,
  balanceOutstanding,
  formatPrice,
} from "../../Helpers/invoice.js";
import { hexToRgb } from "../../Helpers/style.js";
import { delinquentEvents, unsignedEvents } from "../../Helpers/events.js";

export default {
  data() {
    return {};
  },
  computed: {
    colorTheme() {
      return this.$store.getters.colorTheme;
    },
    events() {
      return this.$store.getters.events("future");
    },
    eventsRetrieved() {
      return this.$store.state.eventsRetrieved;
    },
    user() {
      return this.$store.state.user;
    },
    headingText() {
      return `What's up${this.user ? ", " + this.user.given_name : ""}?`;
    },
    tenant() {
      if (this.user) {
        return this.user.userId === this.user.tenantId;
      } else {
        return false;
      }
    },
    todos() {
      if (this.user) {
        if (!this.tenant && !this.$store.state.todosRetrieved) {
          this.$store.dispatch("getUserTodos");
        }
        return this.$store.getters.todos;
      } else {
        return [];
      }
    },
    alerts() {
      let alerts = [];
      if (delinquentEvents().length > 0) {
        alerts.push(InvoicesOverdue);
      }
      if (unsignedEvents().length > 0) {
        alerts.push(ContractsUnsigned);
      }
      if (this.events.length > 0 && !this.tenant) {
        alerts.push(DaysRemaining);
      }
      if (this.tenant) {
        alerts.push(NewBookings, MonthlyRevenueChange);
      }
      if (alerts.length === 0) {
        alerts.push(NoAlerts);
      }
      return alerts;
    },
    chartData() {
      let events = this.$store.getters.events().filter((x) => {
        let start = () => {
          switch (x.api_version) {
            case "9-28-2022":
              return new Date(x.start_time);
            default:
              return new Date(x.data.startTime);
          }
        };
        return start().getFullYear() === new Date().getFullYear();
      });
      let months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      months = months.map((element, index) => {
        let eventsArr = events.filter((x) => {
          let start = () => {
            switch (x.api_version) {
              case "9-28-2022":
                return new Date(x.start_time);
              default:
                return new Date(x.data.startTime);
            }
          };
          return start().getMonth() === index;
        });
        if (eventsArr.length > 0) {
          return eventsArr;
        } else {
          return [];
        }
      });
      let balancesOutstanding = months.map((x) => {
        if (x.length > 0) {
          return x.reduce((acc, event) => {
            if (!acc) {
              acc = 0;
            }
            return parseInt(acc) + parseInt(balanceOutstanding(event));
          }, 0);
        } else {
          return 0;
        }
      });
      let moneysPaid = months.map((x) => {
        if (x.length > 0) {
          return x.reduce((acc, event) => {
            if (!acc) {
              acc = 0;
            }
            return (
              parseInt(acc) +
              Math.abs(
                parseInt(total(event)) - parseInt(balanceOutstanding(event))
              )
            );
          }, 0);
        } else {
          return 0;
        }
      });
      let totalValue = months.map((x) => {
        if (x.length > 0) {
          return x.reduce((acc, event) => {
            if (!acc) {
              acc = 0;
            }
            return parseInt(acc) + parseInt(total(event));
          }, 0);
        } else {
          return 0;
        }
      });
      return [
        {
          labels: [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ],
          color: this.colorTheme[1],
          data: balancesOutstanding,
          title: "Balances Outstanding",
        },
        {
          labels: [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ],
          color: this.colorTheme[2],
          data: moneysPaid,
          title: "Money Paid",
        },
        {
          labels: [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ],
          color: this.$store.getters.highlightColor,
          data: totalValue,
          title: "Total Volume",
        },
      ];
    },
    mask() {
      let background = this.colorTheme[0];
      let hex = hexToRgb(background);
      return {
        background: `linear-gradient(to right, rgba(${hex.r}, ${hex.g}, ${hex.b}, 1), rgba(${hex.r}, ${hex.g}, ${hex.b}, 0))`,
      };
    },
  },
  methods: {
    chartFormatter(int) {
      return formatPrice(int);
    },
  },
  components: {
    Heading,
    UpcomingEvents,
    Chart,
    NoAlerts,
    NewBookings,
    DaysRemaining,
    ContractsUnsigned,
    ToDo,
    InvoicesOverdue,
    MonthlyRevenueChange,
  },
  props: [],
};
</script>
<style scoped>
@media screen {
  #dashboard-wrapper {
    width: 100%;
    height: fit-content;
    display: flex;
    flex-direction: column;
    overflow: visible;
  }

  #alert-scroller {
    display: flex;
    width: calc(100% + 50px);
    min-height: 190px;
    height: 190px;
    overflow: visible;
    overflow-x: scroll;
  }

  .scroll-mask {
    height: 100%;
    min-width: 60px;
    position: sticky;
    left: 0;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
  }

  .scroll-padding {
    min-width: 60px;
  }

  #dashboard-bottom-half {
    display: flex;
    align-items: flex-start;
    min-height: 50%;
  }

  #todo-list {
    width: 375px;
    min-height: 300px;
    max-height: 450px;
  }
  #upcoming-events {
    width: 100%;
    height: 500px;
    min-width: 100%;
  }

  #chart-container {
    /* margin-top: 60px; */
    width: calc(100% - 450px);
    min-width: calc(100% - 450px);
    height: 250px;
    min-height: 250px;
  }
  @media (max-width: 800px) {
    #alert-scroller {
      width: calc(100% + 25px);
    }

    #dashboard-bottom-half {
      flex-direction: column-reverse;
    }

    #todo-list {
      width: 375px;
      min-height: 300px;
      max-height: 450px;
    }

    #chart-container {
      width: 100%;
      min-width: 100%;
    }
  }
  @media (min-width: 800px) {
    #todo-list {
      width: 375px;
      min-height: 300px;
      max-height: 450px;
    }
    #upcoming-events {
      width: 500px;
      min-width: 500px;
    }

    #chart-container {
      margin-top: 60px;
      width: calc(100% - 450px);
      min-width: calc(100% - 450px);
      height: 250px;
      min-height: 250px;
    }
  }
}
</style>
